import type p5 from 'p5';
import type { PoseData } from '../../../types';
import type { ISceneRenderer, ScenePerson } from './ISceneRenderer';

/** RGB triple used internally for fast tile colour maths (alpha handled separately). */
interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * State for a single split-flap tile on the board.
 *
 * A tile is split horizontally into a top and bottom flap. Each carries its own
 * colour so the board can show the classic two-tone look (dark top, amber
 * bottom) when idle. When the displayed colours need to change, the tile runs a
 * short "flip" animation that swaps colours at the animation midpoint.
 */
interface Tile {
  /** Currently displayed top-flap colour. */
  topColor: RGB;
  /** Currently displayed bottom-flap colour. */
  bottomColor: RGB;
  /** Pending top colour committed at the flip midpoint. */
  nextTop: RGB;
  /** Pending bottom colour committed at the flip midpoint. */
  nextBottom: RGB;
  /** Top colour at the moment the current flip started (the falling flap). */
  prevTop: RGB;
  /** Bottom colour at the moment the current flip started. */
  prevBottom: RGB;
  /** Flip animation progress [0, 1]; >= 1 means settled. */
  flip: number;
  /** Timestamp (ms) when this idle tile should next pick new idle colours. */
  nextFlickerAt: number;
}

/**
 * Departure Board Visualization (SCENE scope).
 *
 * Renders the entire canvas as an old-school split-flap railway departure board:
 * a grid of semi-3D flap tiles on a black background. Idle tiles flick subtly in
 * two tones — the top flap between black and grey, the bottom flap between dark
 * amber and orange — giving the board its mechanical shimmer while reading as
 * dark/black overall. Tiles that fall over a detected person's body flick to
 * that person's bright colour, so people appear as luminous silhouettes spelled
 * out across the board.
 *
 * This is a single canvas-wide renderer (ISceneRenderer): it owns the whole
 * frame, paints its own black background, and draws every detected person.
 */
export class DepartureBoardRenderer implements ISceneRenderer {
  private tiles: Tile[] = [];
  private cols = 0;
  private rows = 0;
  private gridWidth = 0;
  private gridHeight = 0;
  private readonly tileSize = 26;
  private readonly flipDurationMs = 140;
  private lastUpdate = 0;

  /** Idle top-flap palette: black / grey (kept dim so the board reads dark). */
  private readonly idleTop: RGB[] = [
    { r: 10, g: 10, b: 12 },
    { r: 46, g: 47, b: 52 },
  ];
  /** Idle bottom-flap palette: dark amber / orange. */
  private readonly idleBottom: RGB[] = [
    { r: 66, g: 46, b: 10 },
    { r: 112, g: 64, b: 14 },
  ];

  /**
   * Per-tile coverage scratch buffer, rebuilt each frame. Holds the colour a
   * person's body contributes to a tile, or null when the tile is idle.
   */
  private coverage: (RGB | null)[] = [];

  render(p: p5, people: ScenePerson[]): void {
    const now = performance.now();
    if (this.lastUpdate === 0) {
      this.lastUpdate = now;
    }
    const dt = now - this.lastUpdate;
    this.lastUpdate = now;

    this.ensureGrid(p.width, p.height, now);
    this.computeCoverage(people);
    this.updateTiles(now, dt);
    this.renderBoard(p);
  }

  reset(): void {
    this.tiles = [];
    this.cols = 0;
    this.rows = 0;
    this.coverage = [];
    this.lastUpdate = 0;
  }

  /**
   * (Re)build the tile grid when the canvas size changes. Each tile starts in a
   * randomised idle state so the board doesn't flicker in unison.
   */
  private ensureGrid(width: number, height: number, now: number): void {
    const cols = Math.max(1, Math.ceil(width / this.tileSize));
    const rows = Math.max(1, Math.ceil(height / this.tileSize));
    if (cols === this.cols && rows === this.rows && this.tiles.length === cols * rows) {
      this.gridWidth = width;
      this.gridHeight = height;
      return;
    }

    this.cols = cols;
    this.rows = rows;
    this.gridWidth = width;
    this.gridHeight = height;
    const count = cols * rows;
    this.tiles = new Array(count);
    this.coverage = new Array(count);

    for (let i = 0; i < count; i++) {
      const top = this.pick(this.idleTop);
      const bottom = this.pick(this.idleBottom);
      this.tiles[i] = {
        topColor: top,
        bottomColor: bottom,
        nextTop: top,
        nextBottom: bottom,
        prevTop: top,
        prevBottom: bottom,
        flip: 1,
        nextFlickerAt: now + this.randomFlickerDelay(),
      };
      this.coverage[i] = null;
    }
  }

  /**
   * Determine, for each tile, whether a detected person's body covers it and in
   * which colour. Coverage is sampled from each person's torso/limb polygons and
   * head ellipse so the silhouette reads as a filled figure rather than a few
   * scattered landmarks.
   */
  private computeCoverage(people: ScenePerson[]): void {
    this.coverage.fill(null);
    if (people.length === 0) {
      return;
    }

    const w = this.gridWidth;
    const h = this.gridHeight;

    for (const { person } of people) {
      const pose = person.poseData;
      if (!pose.detected) {
        continue;
      }
      const color: RGB = {
        r: person.visualConfig.primaryColor.r,
        g: person.visualConfig.primaryColor.g,
        b: person.visualConfig.primaryColor.b,
      };

      // Stamp body segments as thick capsules and the head as a blob so the
      // covered tiles approximate a solid silhouette.
      this.stampSkeleton(pose, color, w, h);
    }
  }

  /**
   * Mark tiles covered by a person's skeleton: limb/torso segments are stamped
   * as thick lines (capsules) and the head as a filled disc.
   */
  private stampSkeleton(pose: PoseData, color: RGB, w: number, h: number): void {
    const lm = pose.landmarks;
    const segments: Array<[keyof PoseData['landmarks'], keyof PoseData['landmarks']]> = [
      ['leftShoulder', 'rightShoulder'],
      ['leftShoulder', 'leftHip'],
      ['rightShoulder', 'rightHip'],
      ['leftHip', 'rightHip'],
      ['leftShoulder', 'leftElbow'],
      ['leftElbow', 'leftWrist'],
      ['rightShoulder', 'rightElbow'],
      ['rightElbow', 'rightWrist'],
      ['leftHip', 'leftKnee'],
      ['leftKnee', 'leftAnkle'],
      ['rightHip', 'rightKnee'],
      ['rightKnee', 'rightAnkle'],
    ];

    // Torso width drives capsule thickness so the figure scales with the person.
    const shoulderW = Math.abs(lm.rightShoulder.x - lm.leftShoulder.x) * w;
    const radius = Math.max(this.tileSize, shoulderW * 0.18);

    for (const [a, b] of segments) {
      const la = lm[a];
      const lb = lm[b];
      if (la.visibility < 0.5 || lb.visibility < 0.5) {
        continue;
      }
      this.stampCapsule(la.x * w, la.y * h, lb.x * w, lb.y * h, radius, color);
    }

    // Head as a filled disc centred on the nose.
    if (lm.nose.visibility >= 0.5) {
      const headRadius = Math.max(this.tileSize, shoulderW * 0.35);
      this.stampDisc(lm.nose.x * w, lm.nose.y * h, headRadius, color);
    }
  }

  /** Mark all tiles whose centre lies within `radius` of the segment AB. */
  private stampCapsule(ax: number, ay: number, bx: number, by: number, radius: number, color: RGB): void {
    const minX = Math.min(ax, bx) - radius;
    const maxX = Math.max(ax, bx) + radius;
    const minY = Math.min(ay, by) - radius;
    const maxY = Math.max(ay, by) + radius;

    const c0 = Math.max(0, Math.floor(minX / this.tileSize));
    const c1 = Math.min(this.cols - 1, Math.floor(maxX / this.tileSize));
    const r0 = Math.max(0, Math.floor(minY / this.tileSize));
    const r1 = Math.min(this.rows - 1, Math.floor(maxY / this.tileSize));
    const r2 = radius * radius;

    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        const cx = col * this.tileSize + this.tileSize / 2;
        const cy = row * this.tileSize + this.tileSize / 2;
        if (this.distToSegmentSq(cx, cy, ax, ay, bx, by) <= r2) {
          this.coverage[row * this.cols + col] = color;
        }
      }
    }
  }

  /** Mark all tiles whose centre lies within `radius` of point (px, py). */
  private stampDisc(px: number, py: number, radius: number, color: RGB): void {
    const c0 = Math.max(0, Math.floor((px - radius) / this.tileSize));
    const c1 = Math.min(this.cols - 1, Math.floor((px + radius) / this.tileSize));
    const r0 = Math.max(0, Math.floor((py - radius) / this.tileSize));
    const r1 = Math.min(this.rows - 1, Math.floor((py + radius) / this.tileSize));
    const r2 = radius * radius;

    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        const cx = col * this.tileSize + this.tileSize / 2;
        const cy = row * this.tileSize + this.tileSize / 2;
        const dx = cx - px;
        const dy = cy - py;
        if (dx * dx + dy * dy <= r2) {
          this.coverage[row * this.cols + col] = color;
        }
      }
    }
  }

  /** Squared distance from point P to segment AB. */
  private distToSegmentSq(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) {
      const ex = px - ax;
      const ey = py - ay;
      return ex * ex + ey * ey;
    }
    let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx;
    const cy = ay + t * dy;
    const ex = px - cx;
    const ey = py - cy;
    return ex * ex + ey * ey;
  }

  /**
   * Advance every tile's flip animation and decide target colours. A tile whose
   * target colour differs from what it currently shows starts a new flip; idle
   * tiles occasionally re-pick a two-tone idle colour for the mechanical
   * shimmer.
   */
  private updateTiles(now: number, dt: number): void {
    const flipStep = this.flipDurationMs > 0 ? dt / this.flipDurationMs : 1;

    for (let i = 0; i < this.tiles.length; i++) {
      const tile = this.tiles[i]!;
      const cover = this.coverage[i];

      // Decide the colours this tile should be showing.
      let targetTop: RGB;
      let targetBottom: RGB;
      if (cover) {
        // Covered: both flaps glow in the person's colour (top slightly dimmer
        // to retain the split-flap shading).
        targetBottom = cover;
        targetTop = this.scale(cover, 0.55);
      } else {
        // Idle: keep the currently chosen idle tones unless it's time to flicker.
        targetTop = tile.nextTop;
        targetBottom = tile.nextBottom;
        if (now >= tile.nextFlickerAt) {
          targetTop = this.pick(this.idleTop);
          targetBottom = this.pick(this.idleBottom);
          tile.nextFlickerAt = now + this.randomFlickerDelay();
        }
      }

      // Start a new flip if the target differs from the pending committed colour.
      if (!this.sameColor(targetTop, tile.nextTop) || !this.sameColor(targetBottom, tile.nextBottom)) {
        tile.prevTop = tile.topColor;
        tile.prevBottom = tile.bottomColor;
        tile.nextTop = targetTop;
        tile.nextBottom = targetBottom;
        tile.flip = 0;
      }

      // Advance the flip; commit pending colours once past the midpoint.
      if (tile.flip < 1) {
        tile.flip = Math.min(1, tile.flip + flipStep);
        if (tile.flip >= 0.5) {
          tile.topColor = tile.nextTop;
          tile.bottomColor = tile.nextBottom;
        }
      }
    }
  }

  /**
   * Draw the board. Background is painted black, then each tile is rendered as a
   * top and bottom flap with a thin seam and a brief "falling flap" highlight
   * mid-flip to suggest 3D mechanical motion.
   */
  private renderBoard(p: p5): void {
    p.background(0, 0, 0);
    p.noStroke();

    const size = this.tileSize;
    const half = size / 2;
    const gap = 1.5;

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const tile = this.tiles[row * this.cols + col]!;
        const x = col * size;
        const y = row * size;

        // Top flap
        p.fill(tile.topColor.r, tile.topColor.g, tile.topColor.b);
        p.rect(x, y, size - gap, half - gap * 0.5);

        // Bottom flap
        p.fill(tile.bottomColor.r, tile.bottomColor.g, tile.bottomColor.b);
        p.rect(x, y + half, size - gap, half - gap * 0.5);

        // Seam line down the middle for the split-flap look
        p.fill(0, 0, 0, 160);
        p.rect(x, y + half - 0.75, size - gap, 1.5);

        // Mid-flip falling-flap highlight: a shrinking horizontal band that
        // reads as a flap rotating through the horizontal plane.
        if (tile.flip < 1) {
          const phase = tile.flip < 0.5 ? tile.flip / 0.5 : (1 - tile.flip) / 0.5;
          const bandH = (half - gap) * (1 - phase);
          if (bandH > 0.5) {
            const falling = tile.flip < 0.5 ? tile.prevTop : tile.nextBottom;
            p.fill(this.scale(falling, 0.4).r, this.scale(falling, 0.4).g, this.scale(falling, 0.4).b, 220);
            const by = tile.flip < 0.5 ? y + (half - gap) - bandH : y + half;
            p.rect(x, by, size - gap, bandH);
          }
        }
      }
    }
  }

  /** Pick a random colour from a palette. */
  private pick(palette: RGB[]): RGB {
    const idx = Math.floor(Math.random() * palette.length);
    return palette[idx] ?? palette[0]!;
  }

  /** Random delay (ms) before an idle tile next re-picks its colours. */
  private randomFlickerDelay(): number {
    return 400 + Math.random() * 2600;
  }

  /** Multiply an RGB triple by a scalar brightness factor, clamped to [0, 255]. */
  private scale(c: RGB, factor: number): RGB {
    return {
      r: Math.max(0, Math.min(255, Math.round(c.r * factor))),
      g: Math.max(0, Math.min(255, Math.round(c.g * factor))),
      b: Math.max(0, Math.min(255, Math.round(c.b * factor))),
    };
  }

  /** Whether two RGB triples are identical. */
  private sameColor(a: RGB, b: RGB): boolean {
    return a.r === b.r && a.g === b.g && a.b === b.b;
  }
}
